import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { MainLayout } from '@/components/layout/MainLayout';

interface RouteErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

function ErrorCard({
  resetError,
  error,
  showNavigateHint,
}: {
  resetError: () => void;
  error: unknown;
  showNavigateHint: boolean;
}) {
  const { t } = useTranslation();
  const message = error instanceof Error ? error.message : String(error);

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <CardTitle>{t('errors.boundary.title')}</CardTitle>
        <CardDescription>{t('errors.boundary.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button className="w-full" onClick={resetError}>
          {t('errors.boundary.retry')}
        </Button>
        {showNavigateHint && (
          <p className="text-center text-sm text-muted-foreground">
            {t('errors.boundary.navigateHint')}
          </p>
        )}
        {import.meta.env.DEV && (
          <details className="mt-2 rounded-md border border-border p-2 text-xs">
            <summary className="cursor-pointer select-none">Error details (dev)</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words">{message}</pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

export function RouteErrorFallback({ error, resetError }: RouteErrorFallbackProps) {
  const { user } = useAuthContext();

  if (user) {
    return (
      <MainLayout>
        <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-6">
          <ErrorCard resetError={resetError} error={error} showNavigateHint={true} />
        </div>
      </MainLayout>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <ErrorCard resetError={resetError} error={error} showNavigateHint={false} />
    </div>
  );
}
